import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface EmployeeOption {
  value: string;
  label: string;
}

interface Props {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  options: EmployeeOption[];
  placeholder: string;
}

/** Searchable single select for long staff lists, which a plain Select can't filter. */
export function EmployeeSelect({ id, value, onChange, options, placeholder }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()));
  const selected = options.find((o) => o.value === value);

  return (
    <div className="relative">
      <button
        id={id}
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((p) => !p)}
        className="flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-border/70 bg-background px-3 text-sm hover:border-primary/60 focus:outline-none focus:ring-2 focus:ring-primary/25"
      >
        <span className={`truncate ${selected ? 'text-foreground' : 'text-muted-foreground'}`}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[240px] rounded-xl border border-border/60 bg-popover text-popover-foreground shadow-lg">
          <div className="p-2">
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="w-full rounded-lg border border-border/60 bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary/60 focus:outline-none"
            />
          </div>
          <div className="thin-scrollbar max-h-52 overflow-y-auto overscroll-contain pb-1">
            {filtered.length === 0 ? (
              <p className="px-4 py-3 text-xs text-muted-foreground">No results</p>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => { onChange(o.value); setOpen(false); setSearch(''); }}
                  className={`w-full px-4 py-2.5 text-left text-sm hover:bg-accent hover:text-accent-foreground ${o.value === value ? 'font-semibold text-primary' : 'text-foreground'}`}
                >
                  {o.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
